import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

// Scroll-reveal wrapper
export const Reveal = ({ children, delay = 0, y = 28, className = "", as = "div" }) => {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </MotionTag>
  );
};

// Kinetic line-by-line masked reveal for headings
export const KineticHeading = ({ lines, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <h1 className={className} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-1" aria-hidden="true">
          <motion.span
            className="block"
            initial={reduce ? { opacity: 0 } : { y: "110%" }}
            animate={reduce ? { opacity: 1 } : { y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
};

// Animated number counter (only counts when a numeric value is provided)
export const Counter = ({ value, placeholder, suffix = "", duration = 1800 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (value == null || !inView) return;
    if (reduce) { setDisplay(value); return; }
    let raf; const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, reduce]);

  return (
    <span ref={ref}>
      {value == null ? placeholder : `${display.toLocaleString()}${suffix}`}
    </span>
  );
};

export const SectionHeading = ({ overline, title, intro, center = false, light = false, id }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl ${center ? "" : ""}`} id={id}>
    {overline && <Reveal><span className="overline">{overline}</span></Reveal>}
    <Reveal delay={0.05}>
      <h2 className={`mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] tracking-tight text-balance ${light ? "!text-white" : ""}`}>
        {title}
      </h2>
    </Reveal>
    {intro && (
      <Reveal delay={0.1}>
        <p className={`mt-5 text-base sm:text-lg leading-relaxed ${light ? "text-white/80" : "text-brand-ink/80"}`}>{intro}</p>
      </Reveal>
    )}
  </div>
);

// Custom eye cursor (desktop only)
export const EyeCursor = () => {
  const dotRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const dot = dotRef.current;
    let x = 0, y = 0, cx = 0, cy = 0, raf;
    const move = (e) => { x = e.clientX; y = e.clientY;
      const t = e.target.closest("a,button,[data-cursor='hover']");
      dot.classList.toggle("hovering", !!t);
    };
    const loop = () => { cx += (x - cx) * 0.2; cy += (y - cy) * 0.2;
      if (dot) dot.style.transform = `translate(${cx}px, ${cy}px) translate(-50%,-50%)`;
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("mousemove", move);
    loop();
    return () => { window.removeEventListener("mousemove", move); cancelAnimationFrame(raf); };
  }, []);
  return <div ref={dotRef} className="cursor-dot" aria-hidden="true" />;
};
